/**
 * laylo-coffee-rail.js
 * Web Component: <laylo-coffee-rail>
 *
 * Manages active card state and indicator pip interaction
 * for the Laylo seasonal coffee section.
 *
 * Responsibilities:
 *   1. Click on a card → mark it active (aria-pressed, is-active class)
 *   2. Click on a pip  → scroll the corresponding card into view
 *   3. Scroll rail     → update active pip to reflect scroll position
 *
 * Progressive enhancement:
 *   Without JS — all cards are visible and scrollable. Pips render as
 *   static decorative dots. No content is hidden. Fully readable.
 *
 * Performance:
 *   - Single delegated click listener on the rail container
 *   - Single delegated click listener on the pip nav
 *   - IntersectionObserver for scroll-position pip sync — no scroll listener
 *   - DOM reads batched before writes — no layout thrashing
 *   - CSS classes drive all visual state — no inline style writes
 *   - No library dependencies
 */

class LayloCoffeeRail extends HTMLElement {
  connectedCallback() {
    this._rail = this.querySelector('.laylo-coffee__rail');
    this._cards = this.querySelectorAll('.laylo-coffee-card');
    this._pips = this.querySelectorAll('.laylo-coffee__pip');

    if (!this._rail || !this._cards.length) return;

    // Set first card active on init
    this._setActive(this._cards[0]);

    // Delegated click on rail — card activation
    this._rail.addEventListener('click', (e) => {
      const card = e.target.closest('.laylo-coffee-card');
      if (!card) return;
      this._setActive(card);
    });

    // Keyboard: Enter or Space on a focused card activates it
    this._rail.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('.laylo-coffee-card');
      if (!card) return;
      e.preventDefault();
      this._setActive(card);
    });

    // Delegated click on pips — scroll card into view
    if (this._pips.length) {
      const pipNav = this.querySelector('.laylo-coffee__pips');
      if (pipNav) {
        pipNav.addEventListener('click', (e) => {
          const pip = e.target.closest('.laylo-coffee__pip');
          if (!pip) return;
          const index = parseInt(pip.dataset.index, 10);
          const card = this._cards[index];
          if (!card) return;
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          this._setActive(card);
        });
      }
    }

    // IntersectionObserver — sync pip to scroll position.
    // Fires when a card enters the rail viewport (threshold 0.5 = half visible).
    // No scroll listener — compositor thread only.
    if ('IntersectionObserver' in window && this._pips.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const index = parseInt(entry.target.dataset.index, 10);
            this._syncPips(index);
          });
        },
        {
          root: this._rail,
          threshold: 0.5,
        }
      );
      this._cards.forEach(card => observer.observe(card));
      this._observer = observer;
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  /**
   * Set a card as active.
   * Batch reads before writes — no layout thrashing.
   * @param {HTMLElement} activeCard
   */
  _setActive(activeCard) {
    // Batch reads
    const cards = [...this._cards];
    const index = cards.indexOf(activeCard);

    // Batch writes
    cards.forEach((card, i) => {
      const isActive = card === activeCard;
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      card.classList.toggle('is-active', isActive);
    });

    this._syncPips(index);
  }

  /**
   * Sync pip active state to a given index.
   * @param {number} index
   */
  _syncPips(index) {
    if (!this._pips.length) return;
    this._pips.forEach((pip, i) => {
      pip.classList.toggle('is-active', i === index);
      pip.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

customElements.define('laylo-coffee-rail', LayloCoffeeRail);
